import React, { FC, useState, useEffect } from 'react';
import ShareIcon from './icons/ShareIcon';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import CopyToClipboard from 'react-copy-to-clipboard';
import LinkCopy from './icons/social/LinkCopy';
import Mail from './icons/social/Mail';
import { EmailShareButton, FacebookShareButton, PinterestShareButton, WhatsappShareButton } from 'react-share';
import Whatsapp from './icons/social/Whatsapp';
import Pinterest from './icons/social/Pinterest';
import Facebook from './icons/social/Facebook';
import { Box } from '@rebass/grid';
import Caret from './icons/Caret';

interface Props {
    share: {
        title: string | undefined;
        url: string;
        text: string;
    };
    image: string;
}
const ShareButton: FC<React.PropsWithChildren<Props>> = ({ share, image }: Props) => {
    const [sharable, setSharable] = useState(false);

    useEffect(() => {
        if (!sharable && 'share' in navigator) {
            setSharable(true);
        }
    }, [sharable]);

    const content = (
        <>
            <ShareIconWrapper>
                <StyledShareIcon />
                <strong>
                    <FormattedMessage defaultMessage="Delen" />
                </strong>
                <StyledCaret className="lt-m" sharable={sharable} />
                <StyledCaret className="gt-m" />
            </ShareIconWrapper>
            <Dropdown>
                <StyledButton type="button">
                    <CopyToClipboard text={share.url}>
                        <IconWrapper>
                            <LinkCopy />
                            <Box pl={3}>
                                <FormattedMessage defaultMessage="Link Kopiëren" />
                            </Box>
                        </IconWrapper>
                    </CopyToClipboard>
                </StyledButton>
                <EmailShareButton url={share.url} subject={share.title} body="">
                    <IconWrapper>
                        <Mail />
                        <Box pl={3}>
                            <FormattedMessage defaultMessage="Mail" />
                        </Box>
                    </IconWrapper>
                </EmailShareButton>
                <WhatsappShareButton url={share.url} title={share.title} separator=":: ">
                    <IconWrapper>
                        <Whatsapp />
                        <Box pl={3}>
                            <FormattedMessage defaultMessage="Whatsapp" />
                        </Box>
                    </IconWrapper>
                </WhatsappShareButton>
                <PinterestShareButton url={share.url} media={image} className="Demo__some-network__share-button">
                    <IconWrapper>
                        <Pinterest />
                        <Box pl={3}>
                            <FormattedMessage defaultMessage="Pinterest" />
                        </Box>
                    </IconWrapper>
                </PinterestShareButton>
                <FacebookShareButton url={share.url} quote={share.title}>
                    <IconWrapper>
                        <Facebook />
                        <Box pl={3}>
                            <FormattedMessage defaultMessage="Facebook" />
                        </Box>
                    </IconWrapper>
                </FacebookShareButton>
            </Dropdown>
        </>
    );

    return (
        <Box pb={[7, 8]}>
            <Wrapper
                className="lt-m"
                sharable={sharable}
                onClick={() => {
                    if (sharable) {
                        navigator.share(share);
                    }
                }}
            >
                {content}
            </Wrapper>
            <Wrapper className="gt-m">{content}</Wrapper>
        </Box>
    );
};

export default ShareButton;

const Wrapper = styled.div<{ sharable?: boolean }>`
    color: ${({ theme }) => theme.colors.primary['60']};
    position: relative;
    display: inline-block;

    > div {
        display: none;
    }

    &:hover {
        > div {
            ${({ sharable }) => !sharable && 'display: flex;'};
        }
    }
`;

const StyledShareIcon = styled(ShareIcon)`
    width: 2rem;
    height: 2rem;
    margin-right: 1.2rem;
`;

const IconWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    border-bottom: 0.1rem solid ${({ theme }) => theme.colors.neutral['20']};
    font-weight: 500;
    padding: 1.5rem 2.4rem;
    color: ${({ theme }) => theme.colors.primary['80']};
    border-radius: 0.4rem;
    svg {
        width: 2rem;
        height: 2rem;
    }

    &:hover {
        background-color: ${({ theme }) => theme.colors.neutral['10']};
        color: ${({ theme }) => theme.colors.primary['60']};
    }
`;

const Dropdown = styled.div`
    border: 0.1rem solid ${({ theme }) => theme.colors.neutral['20']};
    box-shadow: 0 0.2rem 0.4rem 0 rgba(59, 118, 160, 0.02), 0 0.4rem 1.2rem 0 rgba(59, 118, 160, 0.04);
    border-radius: 0.8rem;
    flex-direction: column;
    max-width: 30rem;
    background-color: ${({ theme }) => theme.colors.neutral['0']};
    z-index: 3;
    overflow: hidden;
    display: none;
    position: absolute;
    white-space: nowrap;
`;

const ShareIconWrapper = styled.span`
    display: flex;
    align-items: center;
    padding-bottom: 0.4rem;
`;

const StyledCaret = styled(Caret)<{ sharable?: boolean }>`
    padding-left: 0.4rem;
    width: 1.6rem;
    height: 1.6rem;
    display: ${({ sharable }) => (sharable ? 'none' : 'block')};
`;

const StyledButton = styled.button`
    background-color: transparent;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
`;
